/**
 * AudienceToolHero
 * ─────────────────────────────────────────────────────────────────────────────
 * Panneau éditorial gauche (split-screen) pour les pages /[audience]/[outil].
 *
 * Structure :
 *  ┌─────────────────────────┬──────────────────────────────────┐
 *  │  Eyebrow / H1 / intro   │   {children} = composant funnel  │
 *  │  Benefits               │                                  │
 *  └─────────────────────────┴──────────────────────────────────┘
 *
 * Le contenu éditorial vient de `audience-tool-content.ts`.
 * Le funnel est passé en `children` pour garder la page maître du rendu.
 */
import { type ReactNode } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { Container } from '../layout/Container';
import { MeshBackground } from '../motion/MeshBackground';
import { Reveal } from '../motion/Reveal';
import { ToolPageSubHeader } from '../layout/ToolPageLayout';
import { type AudienceToolContent } from '../../config/audience-tool-content';
import { cn } from '../../lib/cn';

interface AudienceToolHeroProps {
  content: AudienceToolContent;
  /** Libellé affiché dans la mini-barre de sous-navigation (retour). */
  backHref?: string;
  backLabel?: string;
  /** Durée estimée affichée à droite de la sous-barre. */
  duration?: string;
  /** Le composant funnel (rendu dans le panneau droit). */
  children: ReactNode;
}

export function AudienceToolHero({
  content,
  backHref = '/outils',
  backLabel = 'Tous les outils',
  duration = '2-3 min',
  children,
}: AudienceToolHeroProps) {
  return (
    <div className="relative bg-paper">
      <ToolPageSubHeader
        toolLabel={content.eyebrow}
        backHref={backHref}
        backLabel={backLabel}
        rightSlot={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
            <Clock className="h-3 w-3" strokeWidth={2} />
            ~{duration}
          </span>
        }
      />
      <MeshBackground variant="warm" intensity="soft" withGrid />

      {/* Split-screen layout */}
      <div className="relative flex flex-col lg:flex-row">

        {/* ── Panneau gauche — contexte éditorial ── */}
        <div className="relative flex flex-col justify-start border-b border-stone-200/70 bg-paper/80 px-6 pb-8 pt-10 lg:sticky lg:top-0 lg:h-screen lg:w-[40%] lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-10 lg:pt-16">
          <Reveal>
            {/* Eyebrow */}
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-700">
              {content.eyebrow}
            </p>

            {/* H1 */}
            <h1 className={cn(
              'mt-3 font-display text-2xl font-bold leading-tight tracking-tight text-ink md:text-3xl',
            )}>
              {content.headline}{' '}
              <span className="bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-transparent">
                {content.headlineAccent}
              </span>
            </h1>

            {/* Intro */}
            <p className="mt-4 text-[15px] leading-relaxed text-stone-600">
              {content.intro}
            </p>

            {/* Benefits */}
            <ul className="mt-5 flex flex-col gap-2">
              {content.benefits.map((benefit) => (
                <li key={benefit} className="inline-flex items-start gap-2 text-[13.5px] text-stone-600">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" strokeWidth={2} />
                  {benefit}
                </li>
              ))}
            </ul>

            {/* Trust note */}
            {content.trustNote && (
              <p className="mt-5 text-[12px] text-stone-400">{content.trustNote}</p>
            )}
          </Reveal>
        </div>

        {/* ── Panneau droit — funnel ── */}
        <div className="flex-1 px-4 py-10 lg:px-12 lg:py-16">
          <Reveal delay={0.1}>
            {children}
          </Reveal>
        </div>
      </div>
    </div>
  );
}

/**
 * Version sans split-screen (une seule colonne) pour les outils
 * n'ayant pas de funnel à gauche (e.g. TrajectoryChart).
 * Affiche le hero en bandeau avant le contenu principal.
 */
export function AudienceToolHeroBanner({
  content,
  backHref = '/outils',
  backLabel = 'Tous les outils',
  duration,
  children,
}: AudienceToolHeroProps) {
  return (
    <div className="relative bg-paper">
      <ToolPageSubHeader
        toolLabel={content.eyebrow}
        backHref={backHref}
        backLabel={backLabel}
        rightSlot={
          duration ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
              <Clock className="h-3 w-3" strokeWidth={2} />
              ~{duration}
            </span>
          ) : undefined
        }
      />
      <MeshBackground variant="warm" intensity="soft" withGrid />

      <Container className="relative pb-6 pt-10 lg:pt-14">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-700">
            {content.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight text-ink md:text-3xl lg:text-4xl">
            {content.headline}{' '}
            <span className="bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-transparent">
              {content.headlineAccent}
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-stone-600">
            {content.intro}
          </p>
        </Reveal>
      </Container>

      {children}
    </div>
  );
}
